'use client';

import { useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Paperclip } from 'lucide-react';
import { attachmentAccept, maxAttachmentsPerDaemonTrigger, type PendingAttachmentDraft } from '../types/attachment';
import { Spinner } from './loading/Spinner';
import { useAttachmentDraftUpload } from './AttachmentDraftUpload';

type AttachmentAddButtonProps = {
  drafts: PendingAttachmentDraft[];
  onChange: (drafts: PendingAttachmentDraft[]) => void;
  max?: number;
  disabled?: boolean;
};

export function AttachmentAddButton({
  drafts,
  onChange,
  max = maxAttachmentsPerDaemonTrigger,
  disabled = false,
}: AttachmentAddButtonProps) {
  const t = useTranslations('components.appLayout');
  const inputRef = useRef<HTMLInputElement>(null);
  const { addFiles, canAdd, count, uploadingCount } = useAttachmentDraftUpload({
    drafts,
    onChange,
    max,
    disabled,
  });

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-sm gap-1.5"
        onClick={() => inputRef.current?.click()}
        disabled={!canAdd}
        aria-label={t('attachmentAddTooltip')}
        title={
          disabled
            ? t('attachmentAddDisabledTooltip')
            : uploadingCount > 0
              ? t('attachmentAddUploadingTooltip', { count: uploadingCount })
              : count >= max
                ? t('attachmentAddMaxTooltip', { max })
                : t('attachmentAddTooltip')
        }
      >
        {uploadingCount > 0 ? <Spinner /> : <Paperclip className="h-3.5 w-3.5" />}
        <span className="text-xs font-medium">
          {count}/{max}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple
        accept={attachmentAccept}
        onChange={(event) => {
          if (event.target.files) void addFiles(event.target.files);
          event.currentTarget.value = '';
        }}
      />
    </>
  );
}
